/*
 * @providesModule CancelWithdrawal
 *
 */

import {useMutation, useQuery} from '@tanstack/react-query';
import {SKZ_QUERY_KEYS} from '@api/ReactQueryClient';
import React, {Fragment, useMemo, useState} from 'react';
import {View, Text, TouchableOpacity, StyleProp, ViewStyle} from 'react-native';
import I18n, {_m} from 'INTL';
import {stylesFn} from './CancelWithdrawal.styles';
import Label from 'Label';
import SkillzImage from 'SkillzImage';
import PaymentsAPIController from 'PaymentsAPIController';
import {type ServerPaymentsResponse} from 'StoreInfo';
import LoadingIndicator from 'LoadingIndicator';
import {DialogV3} from 'DialogV3';
import ModalNavigationController from 'ModalNavigationController';
import {
  cancelAllWithdrawals,
  CancelAllWithdrawalsResponse,
  type WithdrawalRequest,
} from '@api/Withdrawal';
import NavigationManager from 'NavigationManager';
import * as Animatable from 'react-native-animatable';

type CancelWithdrawalProps = {
  containerStyleOverride?: object | StyleProp<ViewStyle>;
  position?: 'top' | 'bottom';
};

export default function CancelWithdrawal({
  containerStyleOverride,
  position,
}: CancelWithdrawalProps) {
  const styles = stylesFn(containerStyleOverride as object);
  const animationDirection = !!position
    ? position === 'top'
      ? 'slideInDown'
      : 'slideInUp'
    : undefined;

  const {
    data: pendingWithdrawals = [],
    isLoading,
    isInitialLoading,
  } = useQuery(
    [SKZ_QUERY_KEYS.PAYMENT_DATA],
    () => PaymentsAPIController.getPaymentsInformation(false),
    {
      select: (data: ServerPaymentsResponse) => data?.open_withdrawal_requests ?? [],
    }
  );

  const cancelAllWithdrawalsMutation = useMutation(() => cancelAllWithdrawals(pendingWithdrawals), {
    onMutate: () => LoadingIndicator.show(),
    onSettled: () => LoadingIndicator.hide(),
    onSuccess: (response) => {
      ModalNavigationController.hideModal(() => handleCancelAllWithdrawalsSuccess(response));
    },
    onError: (error) => {
      console.error('CancelWithdrawal: Failed to cancel all withdrawals:', error);
      ModalNavigationController.showModal(<ErrorModal onDismiss={exitFlow} />);
    },
  });

  const sumOfPendingWithdrawals = useMemo(() => {
    if (!pendingWithdrawals) {
      return 0;
    }

    return pendingWithdrawals.reduce((acc: number, withdrawal: WithdrawalRequest) => {
      return acc + withdrawal.amount;
    }, 0);
  }, [pendingWithdrawals]);

  function handleCancelPress() {
    const sumOfBonuses = pendingWithdrawals.reduce(
      (acc: number, withdrawal: WithdrawalRequest) => acc + withdrawal.bonus,
      0
    );

    ModalNavigationController.showModal(
      <ConfirmationModal
        totalAmount={sumOfPendingWithdrawals}
        totalBonus={sumOfBonuses}
        onDismiss={() => ModalNavigationController.hideModal()}
        onConfirmPress={() => cancelAllWithdrawalsMutation.mutate()}
      />
    );
  }

  function handleCancelAllWithdrawalsSuccess(response: CancelAllWithdrawalsResponse) {
    const {successfulWithdrawals, failedWithdrawals} = response;

    if (!successfulWithdrawals?.length) {
      ModalNavigationController.showModal(<ErrorModal onDismiss={exitFlow} />);
      return;
    }

    const totalAmount = successfulWithdrawals.reduce(
      (acc: number, withdrawal: WithdrawalRequest) => {
        return acc + withdrawal.amount;
      },
      0
    );
    const totalBonus = successfulWithdrawals.reduce(
      (acc: number, withdrawal: WithdrawalRequest) => {
        return acc + withdrawal.bonus;
      },
      0
    );

    ModalNavigationController.showModal(
      <SuccessModal
        totalAmount={totalAmount}
        totalBonus={totalBonus}
        failedWithdrawals={failedWithdrawals.length}
        onDismiss={exitFlow}
      />
    );
  }

  function exitFlow() {
    ModalNavigationController.hideModal();
    NavigationManager.popToRoot();
  }

  if (!pendingWithdrawals || pendingWithdrawals.length === 0 || isInitialLoading) {
    return <Fragment />;
  }

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.contentContainer}>
          <LoadingIndicator isInline />
        </View>
      </View>
    );
  }

  return (
    <Animatable.View
      style={styles.container}
      animation={animationDirection}
      duration={200}
      useNativeDriver={true}
      easing={'ease-in'}
    >
      <View style={styles.contentContainer} accessibilityLabel='Cancel Withdrawals'>
        <Text adjustsFontSizeToFit allowFontScaling={false}>
          <Text style={styles.contentText}>
            {_m('{0} Withdrawals Processing - Total: ', pendingWithdrawals.length)}
          </Text>
          <Text style={styles.withdrawAmountText} adjustsFontSizeToFit>
            ${sumOfPendingWithdrawals.toFixed(2)}
          </Text>
        </Text>
      </View>

      <TouchableOpacity style={styles.viewDetailsButton} onPress={handleCancelPress}>
        <Label style={styles.viewDetailsText}>{_m('Cancel')}</Label>
        <SkillzImage
          style={styles.chevronIcon}
          source={'ic_chevron_right_skz.png'}
          tintColor={'#367BCE'}
        />
      </TouchableOpacity>
    </Animatable.View>
  );
}

type ConfirmationModalProps = {
  totalAmount: number;
  totalBonus: number;
  onConfirmPress: () => void;
  onDismiss: () => void;
};
function ConfirmationModal({
  totalAmount,
  totalBonus,
  onConfirmPress,
  onDismiss,
}: ConfirmationModalProps) {
  const styles = stylesFn();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const handleConfirmPress = () => {
    setIsProcessing(true);
    onConfirmPress();
  };

  return (
    <DialogV3 onClose={onDismiss}>
      <View style={styles.confirmationContainer}>
        <Text style={styles.confirmationTitle} allowFontScaling={false}>
          {_m('Cancel Withdrawals?')}
        </Text>

        <View style={styles.confirmationAmountContainer}>
          <Text style={styles.confirmationAmountText} allowFontScaling={false}>
            {_m('Total Cash:')}{' '}
            <Text style={styles.confirmationAmountTextCash} allowFontScaling={false}>
              {I18n.getCurrencyString(totalAmount, '$', false, false)}
            </Text>
          </Text>
          <Text style={styles.confirmationAmountText} allowFontScaling={false}>
            {_m('Total Bonus Cash:')}{' '}
            <Text style={styles.confirmationAmountTextCash} allowFontScaling={false}>
              {I18n.getCurrencyString(totalBonus, '$', false, false)}
            </Text>
          </Text>
        </View>

        <Text style={styles.confirmationWarningText} allowFontScaling={false}>
          {_m('All pending withdrawals will be cancelled.\nThis action cannot be undone.')}
        </Text>

        <TouchableOpacity
          style={[styles.confirmButton, isProcessing && styles.confirmButtonDisabled]}
          onPress={handleConfirmPress}
          disabled={isProcessing}
        >
          <Text style={styles.confirmButtonText} allowFontScaling={false}>
            {_m('Confirm')}
          </Text>
        </TouchableOpacity>
      </View>
    </DialogV3>
  );
}
type SuccessModalProps = {
  totalAmount: number;
  totalBonus: number;
  failedWithdrawals: number;
  onDismiss: () => void;
};
function SuccessModal({totalAmount, totalBonus, failedWithdrawals, onDismiss}: SuccessModalProps) {
  const styles = stylesFn();
  return (
    <DialogV3 onClose={onDismiss}>
      <View style={styles.confirmationContainer}>
        <Text style={styles.confirmationTitle} allowFontScaling={false}>{_m('Withdrawal\nSuccessfully Canceled')}</Text>

        <View style={styles.confirmationAmountContainer}>
          <Text style={styles.confirmationAmountText} allowFontScaling={false}>
            {_m('Total Cash:')}{' '}
            <Text style={styles.confirmationAmountTextCash} allowFontScaling={false}>
              {I18n.getCurrencyString(totalAmount, '$', false, false)}
            </Text>
          </Text>
          <Text style={styles.confirmationAmountText} allowFontScaling={false}>
            {_m('Total Bonus Cash:')}{' '}
            <Text style={styles.confirmationAmountTextCash} allowFontScaling={false}>
              {I18n.getCurrencyString(totalBonus, '$', false, false)}
            </Text>
          </Text>
        </View>

        {failedWithdrawals > 0 && (
          <Text style={styles.confirmationWarningText} allowFontScaling={false}>
            {_m('{0} withdrawals could not be cancelled.', failedWithdrawals)}
          </Text>
        )}

        <TouchableOpacity style={styles.confirmButton} onPress={onDismiss}>
          <Text style={styles.confirmButtonText} allowFontScaling={false}>{_m('Okay')}</Text>
        </TouchableOpacity>
      </View>
    </DialogV3>
  );
}

type ErrorModalProps = {
  onDismiss: () => void;
};
function ErrorModal({onDismiss}: ErrorModalProps) {
  const styles = stylesFn();
  return (
    <DialogV3 onClose={onDismiss}>
      <View style={styles.confirmationContainer}>
        <Text style={styles.confirmationTitle} allowFontScaling={false}>
          {_m('Failed to Cancel Withdrawal')}
        </Text>

        <Text style={styles.confirmationWarningText} allowFontScaling={false}>
          {_m('There was an error cancelling your withdrawal.\nPlease try again later.')}
        </Text>

        <TouchableOpacity style={styles.confirmButton} onPress={onDismiss}>
          <Text style={styles.confirmButtonText} allowFontScaling={false}>
            {_m('Okay')}
          </Text>
        </TouchableOpacity>
      </View>
    </DialogV3>
  );
}
